// THE FLOATING CORE's rules (#6779, prototype `:4151-4306`): when it shows, how wide it is,
// and how the keys resize it. Pure but for `readPip` / `writePip`, the per-viewer size
// preference under the prototype's key `fleet-live-pip-v2` as `{ w, min }` (`w: 0` is the
// default width), default `{ w: 0, min: true }`: it STARTS COLLAPSED. A bad stored value
// falls back to that default.
//
// WHEN IT SHOWS ({@link pipShouldShow}): the Core panel is shown and its bottom edge is
// above 40 px from the viewport top, no dialog of the Fleet page is open (the machine
// details panel included, read from the page's own state, never by querying the DOM for a
// scrim: prototype quirk 3), and it was not closed with "Hide the floating Core" since the
// panel was last in view.

import { z } from 'zod';
import { readLocalPref, writeLocalPref } from './local-pref';

export const PIP_KEY = 'fleet-live-pip-v2';

export const PIP_MIN_W = 220;
export const PIP_MAX_W = 560;
/** How often the floating Core checks whether it should show. */
export const PIP_CHECK_MS = 120;
/** The Core panel's bottom edge above this many px from the viewport top: scrolled past. */
export const PIP_PAST_PX = 40;
/** At this width or wider, the stats use the larger size. */
export const PIP_BIG_W = 400;
/** Below this viewport width the floating Core is narrower and its "Live" tag hides. */
export const PIP_NARROW_BELOW_PX = 480;

/**
 * The id of #6773's transfer stack slot, directly above the floating Core, so the transfers
 * mount there instead of finding the floating Core through the DOM (prototype quirk 3).
 */
export const FLEET_TRANSFER_SLOT_ID = 'fleet-live-transfers';

export type PipSize = { readonly w: number; readonly min: boolean };

export const DEFAULT_PIP: PipSize = { w: 0, min: true };

const PipSchema = z.object({ w: z.number().nonnegative(), min: z.boolean() });

export function readPip(): PipSize {
  return readLocalPref(PIP_KEY, PipSchema, DEFAULT_PIP);
}

export function writePip(size: PipSize): void {
  writeLocalPref(PIP_KEY, size);
}

/** The default width: 300 px, 236 px below a 480 px viewport. */
export function defaultPipWidth(viewportW: number): number {
  return viewportW < PIP_NARROW_BELOW_PX ? 236 : 300;
}

/** The widest it may be: 560 px, or the viewport less 32 px; never under 220. */
export function maxPipWidth(viewportW: number): number {
  return Math.max(PIP_MIN_W, Math.min(PIP_MAX_W, viewportW - 32));
}

/** The width it is drawn at. */
export function pipWidth(size: PipSize, viewportW: number): number {
  const w = size.w || defaultPipWidth(viewportW);
  return Math.round(Math.max(PIP_MIN_W, Math.min(maxPipWidth(viewportW), w)));
}

/** The canvas height for a width (expanded). */
export function pipCanvasHeight(w: number): number {
  return Math.round(w * 0.57);
}

/** The "W × H" tag shown while resizing, H = W × 0.57 + 104. */
export function pipSizeTag(w: number): { readonly w: number; readonly h: number } {
  return { w: Math.round(w), h: Math.round(w * 0.57 + 104) };
}

/**
 * The width after a resize key, or `null` for a key that does not resize: ArrowLeft and
 * ArrowUp grow it by 20 px, ArrowRight and ArrowDown shrink it, Shift makes the step 60.
 */
export function stepPipWidth(
  size: PipSize,
  key: string,
  shift: boolean,
  viewportW: number,
): number | null {
  const step = shift ? 60 : 20;
  const cur = pipWidth(size, viewportW);
  if (key === 'ArrowLeft' || key === 'ArrowUp') return Math.min(maxPipWidth(viewportW), cur + step);
  if (key === 'ArrowRight' || key === 'ArrowDown') return Math.max(PIP_MIN_W, cur - step);
  return null;
}

/**
 * The width a pointer drag makes, from where it started (prototype `:4243`). The handle is
 * the top-left corner: `dx` / `dy` are the pointer's travel LEFT / UP, so positive grows and
 * negative shrinks. The DOMINANT move wins by magnitude with its sign kept (`dy` at the
 * 0.57 aspect); a signed `Math.max` picked 0 over any shrink along one axis, so a drag
 * right or down alone could not shrink it (review thread 4407441506).
 */
export function dragPipWidth(startW: number, dx: number, dy: number, viewportW: number): number {
  const fromY = dy / 0.57;
  const d = Math.abs(dx) >= Math.abs(fromY) ? dx : fromY;
  return Math.round(Math.max(PIP_MIN_W, Math.min(maxPipWidth(viewportW), startW + d)));
}

export type PipCheck = {
  /** Whether the Core panel is shown (its switch on). */
  readonly coreShown: boolean;
  /** The Core panel's box in the viewport, or `null` when it is not laid out. */
  readonly rect: { readonly top: number; readonly bottom: number } | null;
  /** Any dialog of the Fleet page open, the machine details panel included. */
  readonly dialogOpen: boolean;
  /** Closed with "Hide the floating Core" since the panel was last in view. */
  readonly dismissed: boolean;
  readonly viewportH?: number;
};

/** Whether it shows, and the dismissal carried forward (forgotten once the panel is in view). */
export function pipShouldShow(c: PipCheck): {
  readonly show: boolean;
  readonly dismissed: boolean;
} {
  if (!c.coreShown || c.rect === null) return { show: false, dismissed: false };
  const vh = c.viewportH ?? globalThis.innerHeight ?? Number.POSITIVE_INFINITY;
  const inView = c.rect.bottom > 0 && c.rect.top < vh;
  const dismissed = inView ? false : c.dismissed;
  const past = c.rect.bottom < PIP_PAST_PX;
  return { show: past && !dismissed && !c.dialogOpen, dismissed };
}
